import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const safeFilename = (title) => (title || 'clip').replace(/[^a-z0-9-_]+/gi, '_');

const triggerDownload = (href, filename) => {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Encapsulates clip export logic. Returns { exporting, exportClip(clip, accentColor, useSyncedSubs) }.
 * - plain path  → simple GET /stream URL trigger (cookie auth, same-origin)
 * - burned path → POST /export and download the rendered MP4 blob
 */
export const useClipExport = (t) => {
  const [exporting, setExporting] = useState(false);

  const exportPlain = (clip) => {
    triggerDownload(`${API_BASE}/clips/${clip.id}/download`, `${safeFilename(clip.title)}.mp4`);
    toast.success(t('ed.export.toast') || 'Téléchargement démarré');
  };

  const exportBurned = async (clip, accentColor, _extra = {}) => {
    setExporting(true);
    const toastId = toast.loading('Génération du clip avec sous-titres incrustés…');
    try {
      // Single-style export — always legacy path. No preset dimension.
      const resp = await api.post(
        `/clips/${clip.id}/export`,
        { accent_color: accentColor, burn_subs: true },
        { responseType: 'blob', timeout: 120000 },
      );
      const blobUrl = window.URL.createObjectURL(resp.data);
      triggerDownload(blobUrl, `${safeFilename(clip.title)}_captioned.mp4`);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Clip HD prêt à poster sur TikTok 🎬', { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'export', { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  const exportClip = (clip, accentColor, useSyncedSubs, captionsEnabled = true, extra = {}) => {
    if (!clip?.has_file) {
      toast.error(t('ed.export.unavailable') || 'Clip file not available');
      return;
    }
    // No subtitles requested → always plain stream download (no burn-in)
    if (!captionsEnabled) {
      exportPlain(clip);
      return;
    }
    const hasWords = clip.words && clip.words.length > 0;
    if (!hasWords || !useSyncedSubs) {
      exportPlain(clip);
    } else {
      exportBurned(clip, accentColor, extra);
    }
  };

  return { exporting, exportClip };
};
